import { useCallback, useState } from 'react'

import { ProfilingRequestSource } from 'clickNgo/configs/profiling/common'
import {
  ContactStatus,
  ContactWorkflowStatus,
  IContact,
  useReassignFlags,
} from 'clickNgo/entities/contact'
import { mapContactModifiedValues, updateContact } from 'clickNgo/entities/contact/api'
import { reassignContactRoles } from 'clickNgo/features/contact/contactReassignment/lib'
import { getJSendErrorMessages } from 'clickNgo/helpers/api'
import { I375Form } from 'clickNgo/models/crud/forms/i375Form'
import { NotificationController } from 'core-components/library/popup'
import Logger from 'core/utils/Logger'

import { ContactDeactivationError } from '../configs'
import { useReassignment } from '../contexts'
import { FlagToReassign, RoleToReassign } from '../models'

interface UseContactDeactivationParams {
  contact: IContact
  rolesToReassign: RoleToReassign[]
  flagsToReassign: FlagToReassign[]
  onDeactivate: (deactivatedContact: IContact) => void
}

export const useContactDeactivation = ({
  contact,
  rolesToReassign,
  flagsToReassign,
  onDeactivate,
}: UseContactDeactivationParams) => {
  const [loaderState, setLoaderState] = useState({ isLoading: false, message: '' })
  const { reassignFlags } = useReassignFlags()
  const { setFlagsToReassign, setRolesToReassign } = useReassignment()

  const deactivateContact = useCallback(async () => {
    if (rolesToReassign.filter((role) => role.contact).length) {
      try {
        setLoaderState({ isLoading: true, message: 'Reassigning roles...' })
        await reassignContactRoles(rolesToReassign)
        setRolesToReassign([])
      } catch (error) {
        Logger.error(ContactDeactivationError.ReassignRoles, error)
        NotificationController.showError({ message: ContactDeactivationError.ReassignRoles })
      } finally {
        setLoaderState({ isLoading: false, message: '' })
      }
    }

    if (flagsToReassign.filter((flag) => flag.contact).length) {
      try {
        setLoaderState({ isLoading: true, message: 'Reassigning flags...' })
        await reassignFlags(flagsToReassign)
        setFlagsToReassign([])
      } catch (error) {
        Logger.error(ContactDeactivationError.ReassignFlags, error)
        NotificationController.showError({ message: ContactDeactivationError.ReassignFlags })
      } finally {
        setLoaderState({ isLoading: false, message: '' })
      }
    }

    const request: IContact = {
      ...contact,
      contactId: contact.contactId,
      partnerId: contact.partnerId,
      learnerId: contact.learnerId,
      countryOfBusiness: contact.countryOfBusiness,
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email,
      country: contact.country,
      market: contact.market,
      status: ContactStatus.Inactive,
      source: ProfilingRequestSource.ClickAndGoPortal,
      workflowStatus: ContactWorkflowStatus.New,
      modifiedValues: ['status'],
    }

    try {
      setLoaderState({ isLoading: true, message: 'Deactivating contact...' })
      const response = await updateContact(request, { forceRoles: true })

      switch (response.status) {
        case 'fail': {
          NotificationController.showError({
            message: getJSendErrorMessages(response).join('\\n '),
          })
          break
        }
        case 'error': {
          NotificationController.showError({
            message: response.message ? response.message : 'Unknown error occurred',
          })
          break
        }
        case 'success': {
          const updatedContact: IContact = {
            ...contact,
            ...request,
            ...response.data,
            modifiedValues: mapContactModifiedValues(
              response.data.modifiedValues.split(',') as (keyof I375Form)[]
            ),
            modified: new Date().toISOString(),
          }

          await onDeactivate({
            ...updatedContact,
            status: ContactStatus.Inactive,
          })
          break
        }
      }
    } catch (error) {
      Logger.error(ContactDeactivationError.DeactivateContact, error)
      NotificationController.showError({ message: ContactDeactivationError.DeactivateContact })
    } finally {
      setLoaderState({ isLoading: false, message: '' })
    }
  }, [
    rolesToReassign,
    flagsToReassign,
    contact,
    setRolesToReassign,
    reassignFlags,
    setFlagsToReassign,
    onDeactivate,
  ])

  return { deactivateContact, loaderState }
}
